import { Injectable, inject } from '@angular/core';
import { CanActivateFn, CanMatch, UrlTree } from '@angular/router';
import {
  AuthAccessTokenDetails,
  DashboardResponse,
  UserSessionService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AppAuthRedirectHelper } from './app-auth-redirect-helper';
import { AppAuthTokenFetcher } from './app-auth-token-fetcher';

/**
 * App Auth Guard - Reusable guard for apps requiring:
 * 1. Deltacom client ID auth + getDashBrdData (login verification)
 * 2. App-specific token fetch (for downstream API calls)
 *
 * Configuration:
 * Provide 'OAUTH_FEATURE_CONFIG' with { app, endpointKey } in app.module.ts.
 *
 * Usage in routes:
 * ```typescript
 * import { AppAuthGuard } from '@com-delta-idp/mnx-skymiles-shared';
 * { path: 'protected', canMatch: [AppAuthGuard], loadChildren: ... }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class AppAuthGuard implements CanMatch {
  private userSessionService = inject(UserSessionService);
  private tokenFetcher = inject(AppAuthTokenFetcher);
  private redirectHelper = inject(AppAuthRedirectHelper);

  private _token!: AuthAccessTokenDetails;
  private _dashboardData!: Partial<DashboardResponse>;

  get token(): AuthAccessTokenDetails {
    return this._token;
  }

  get dashboardData(): Partial<DashboardResponse> {
    return this._dashboardData;
  }

  canMatch(): Observable<boolean | UrlTree> {
    return this.userSessionService.getUserSession().pipe(
      switchMap((response: DashboardResponse) => {
        if (response && response.smNumber) {
          this._dashboardData = response;
          return this.fetchAppSpecificToken();
        }
        return this.handleNotLoggedIn();
      }),
      catchError(() => {
        this.redirectHelper.redirect();
        return of(false);
      })
    );
  }

  getToken(): Observable<AuthAccessTokenDetails> {
    if (this._token && this._token.access_token) {
      return of(this._token);
    }
    return this.tokenFetcher.fetchToken().pipe(
      map((token: AuthAccessTokenDetails) => {
        this._token = token;
        return token;
      })
    );
  }

  private fetchAppSpecificToken(): Observable<boolean> {
    if (!this.tokenFetcher.hasConfig()) {
      return of(true);
    }
    return this.tokenFetcher.fetchToken().pipe(
      map((token: AuthAccessTokenDetails) => {
        if (token && token.access_token) {
          this._token = token;
        }
        return true;
      }),
      catchError(() => of(true))
    );
  }

  private handleNotLoggedIn(): Observable<boolean> {
    if (window.location.hostname === 'localhost') {
      return of(true);
    }
    this.redirectHelper.redirect();
    return of(false);
  }
}

/**
 * Functional guard for use in route definitions.
 * Usage: canMatch: [AppAuthGuardFn]
 */
export const AppAuthGuardFn: CanActivateFn = (): Observable<
  boolean | UrlTree
> => {
  return inject(AppAuthGuard).canMatch();
};
